/**
 * SG PreSet — Notice for Controllers 386AG(2)(a) (Corporate)
 */

import {
  formatJurisdictionStatuteDisplay,
  isSgControllerJurisdiction,
  resolveB15CodeToName,
} from './b15JurisdictionDisplay';
import { buildNoticeCorporateContactDataFromPreset } from './adaptControllerEfilingFormData';
import { PRESET_MANUAL_CONTROLLER_CONTACT_CD } from '../../notice-individual/composables/adaptControllerEfilingFormData';
import { formatContactNoDisplay, parseLegacyContactPhone } from './formOptions';

export interface NoticeCorporateFormModel {
  noticeDate: string;
  noticeRecipientName: string;
  companyName: string;
  companyRegistrationNo: string;
  replyToAddress: string;
  noticeSignerName: string;
  controllerCorporateName: string;
  controllerUen: string;
  controllerRegisteredOffice: string;
  controllerAlternateAddress: string;
  controllerEmail: string;
  /** @deprecated 문서용 — prepareNoticeCorporateFormForOutput 에서 갱신 */
  controllerContactNo: string;
  controllerCountryCd: string;
  /** B05 표시명 (예: SINGAPORE +65) — 챗 선택·문서 표시 */
  controllerCountryLabel: string;
  controllerAreaCode: string;
  controllerMobileNo: string;
  controllerLegalForm: string;
  /** 문서용 합성 (jurisdiction — statute) */
  controllerJurisdictionStatute: string;
  /** Jurisdiction 표시명 */
  controllerJurisdiction: string;
  /** Statute (E-Filing · 문서) */
  controllerIncorporationStatute: string;
  /** B15 jurisdiction code — E-Filing select */
  controllerJurisdictionCd: string;
  controllerRegisterName: string;
  controllerRegistrationNo: string;
  controllerBecameDate: string;
  acknowledgementSignerName: string;
  acknowledgementSignerRole: string;
  contactCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyNoticeCorporateForm(): NoticeCorporateFormModel {
  return {
    noticeDate: '',
    noticeRecipientName: '',
    companyName: '',
    companyRegistrationNo: '',
    replyToAddress: '',
    noticeSignerName: '',
    controllerCorporateName: '',
    controllerUen: '',
    controllerRegisteredOffice: '',
    controllerAlternateAddress: '',
    controllerEmail: '',
    controllerContactNo: '',
    controllerCountryCd: '',
    controllerCountryLabel: '',
    controllerAreaCode: '',
    controllerMobileNo: '',
    controllerLegalForm: '',
    controllerJurisdictionStatute: '',
    controllerJurisdiction: '',
    controllerIncorporationStatute: '',
    controllerJurisdictionCd: '',
    controllerRegisterName: '',
    controllerRegistrationNo: '',
    controllerBecameDate: '',
    acknowledgementSignerName: '',
    acknowledgementSignerRole: '',
    contactCd: '',
    contactData: null,
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function looksLikeUuid(s: string): boolean {
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

/** 저장/문서/E-Filing 직전 — 합성 필드·표시명 동기화 */
export function prepareNoticeCorporateFormForOutput(
  model: NoticeCorporateFormModel,
  b15Map: Map<string, string>,
): void {
  const jCd = trim(model.controllerJurisdictionCd);
  const jName = resolveB15CodeToName(jCd, b15Map) || trim(model.controllerJurisdiction);
  const statute = trim(model.controllerIncorporationStatute);
  if (jName) model.controllerJurisdiction = jName;
  model.controllerJurisdictionStatute = formatJurisdictionStatuteDisplay(jCd || jName, statute, b15Map);
  const combinedPhone = formatContactNoDisplay(
    model.controllerCountryCd,
    model.controllerAreaCode,
    model.controllerMobileNo,
  );
  if (combinedPhone) model.controllerContactNo = combinedPhone;
}

export function resolveNoticeCorporateEntityUen(
  model: Pick<NoticeCorporateFormModel, 'companyRegistrationNo'>,
  entityCd = '',
): string {
  const companyUen = trim(model.companyRegistrationNo);
  const ec = trim(entityCd);
  if (companyUen) return companyUen;
  if (ec && !looksLikeUuid(ec)) return ec;
  return '';
}

export function toNoticeCorporateTemplateVariables(
  model: NoticeCorporateFormModel,
  b15Map?: Map<string, string>,
): Record<string, string> {
  if (b15Map) prepareNoticeCorporateFormForOutput(model, b15Map);

  const controllerCorporateName = trim(model.controllerCorporateName);
  const noticeRecipientName =
    trim(model.noticeRecipientName) || controllerCorporateName;
  const entityUen = trim(model.companyRegistrationNo);
  const jurisdictionName = trim(model.controllerJurisdiction)
    || (b15Map ? resolveB15CodeToName(trim(model.controllerJurisdictionCd), b15Map) : '');
  const statute = trim(model.controllerIncorporationStatute);
  const contactDisplay = trim(model.controllerContactNo)
    || formatContactNoDisplay(
      model.controllerCountryCd,
      model.controllerAreaCode,
      model.controllerMobileNo,
    );

  return {
    notice_date: trim(model.noticeDate),
    notice_recipient_name: noticeRecipientName,
    company_name: trim(model.companyName),
    company_registration_no: entityUen,
    entity_uen: entityUen,
    entityUEN: entityUen,
    EntityUEN: entityUen,
    entityRegNo: entityUen,
    reply_to_address: trim(model.replyToAddress),
    notice_signer_name: trim(model.noticeSignerName),
    controller_corporate_name: controllerCorporateName,
    controller_uen: trim(model.controllerUen),
    controller_registered_office: trim(model.controllerRegisteredOffice),
    controller_alternate_address: trim(model.controllerAlternateAddress),
    controller_email: trim(model.controllerEmail),
    controller_contact_no: contactDisplay,
    controller_country_cd: trim(model.controllerCountryCd),
    controller_country_label: trim(model.controllerCountryLabel),
    controller_area_code: trim(model.controllerAreaCode),
    controller_mobile_no: trim(model.controllerMobileNo),
    controller_legal_form: trim(model.controllerLegalForm),
    controller_jurisdiction: jurisdictionName,
    controller_incorporation_statute: statute,
    controller_jurisdiction_statute: trim(model.controllerJurisdictionStatute),
    controller_jurisdiction_cd: trim(model.controllerJurisdictionCd),
    jurisdictionCd: trim(model.controllerJurisdictionCd),
    controller_register_name: trim(model.controllerRegisterName),
    controller_registration_no: trim(model.controllerRegistrationNo),
    controller_became_date: trim(model.controllerBecameDate),
    acknowledgement_signer_name: trim(model.acknowledgementSignerName),
    acknowledgement_signer_role: trim(model.acknowledgementSignerRole),
    contact_cd: trim(model.contactCd),
    contactCd: trim(model.contactCd),
    categoryCode: 'B09001',
    categoryCd: 'B09001',
    signers: '',
    Signers: '',
  };
}

export function syncNoticeCorporateManualContactData(
  model: NoticeCorporateFormModel,
  b15Map?: Map<string, string>,
): void {
  const vars = toNoticeCorporateTemplateVariables(model, b15Map) as Record<string, unknown>;
  model.contactData = buildNoticeCorporateContactDataFromPreset(vars) as Record<string, any>;
}

export function buildNoticeCorporateTaskFormData(
  model: NoticeCorporateFormModel,
  entityCd: string,
  b15Map?: Map<string, string>,
): Record<string, unknown> {
  syncNoticeCorporateManualContactData(model, b15Map);
  const ec = trim(entityCd);
  const entityUen = resolveNoticeCorporateEntityUen(model, ec);
  const vars = toNoticeCorporateTemplateVariables(model, b15Map);
  const contactCd = trim(model.contactCd);
  const contactData = model.contactData ?? undefined;
  const corporateName = trim(model.controllerCorporateName);

  return {
    ...vars,
    entityCd: ec,
    entity: ec,
    contactCd,
    contact_cd: contactCd,
    controllerCd: contactCd,
    controllerName: contactCd,
    companyRegistrationNo: entityUen,
    entityRegNo: entityUen,
    entityName: { value: trim(model.companyName) },
    categoryCode: 'B09001',
    categoryCd: 'B09001',
    legalForm: vars.controller_legal_form,
    jurisdiction: trim(model.controllerJurisdictionCd),
    jurisdictionCd: trim(model.controllerJurisdictionCd),
    incorporationStatute: vars.controller_incorporation_statute,
    corporateEntityRegister: vars.controller_register_name,
    nonAcrAuen: vars.controller_registration_no,
    acrAuen: vars.controller_uen,
    regNo: vars.controller_uen,
    contactRegNo: vars.controller_registration_no,
    CONTACT_REG_NO: vars.controller_registration_no,
    countryCd: trim(model.controllerCountryCd),
    COUNTRY_CD: trim(model.controllerCountryCd),
    areaCode: trim(model.controllerAreaCode),
    AREA_CODE: trim(model.controllerAreaCode),
    mobileNo: trim(model.controllerMobileNo),
    MOBILE_NO: trim(model.controllerMobileNo),
    ...(contactData ? { contactData } : {}),
    ...(corporateName
      ? {
        presetControllerContactOption: {
          title: corporateName,
          value: contactCd || PRESET_MANUAL_CONTROLLER_CONTACT_CD,
        },
      }
      : {}),
  };
}

export function isNoticeCorporateFormComplete(model: NoticeCorporateFormModel): boolean {
  const base = [
    model.noticeDate,
    model.companyName,
    model.replyToAddress,
    model.noticeSignerName,
    model.controllerCorporateName,
    model.controllerRegisteredOffice,
    model.controllerEmail,
    model.controllerCountryCd,
    model.controllerMobileNo,
    model.controllerLegalForm,
    model.controllerJurisdictionCd,
    model.controllerIncorporationStatute,
    model.controllerRegisterName,
    model.controllerBecameDate,
    model.acknowledgementSignerName,
    model.acknowledgementSignerRole,
  ];
  if (isSgControllerJurisdiction(trim(model.controllerJurisdictionCd))) {
    base.push(model.controllerUen);
  }
  return base.every((v) => trim(v).length > 0);
}

export function applyEntityBasicsToNoticeCorporateForm(
  model: NoticeCorporateFormModel,
  ent: Record<string, unknown> | null | undefined,
): void {
  if (!ent) return;
  const name = trim(ent.companyName ?? ent.entityName ?? ent.ENTITY_NAME);
  if (name) model.companyName = name;
  const reg = trim(
    ent.registrationNo ?? ent.regNo ?? ent.REG_NO ?? ent.companyRegistrationNo,
  );
  if (reg) model.companyRegistrationNo = reg;
}

/** 레거시: 합쳐진 jurisdiction/statute·단일 contact no → 분리 필드 */
export function migrateLegacyNoticeCorporateFields(
  model: NoticeCorporateFormModel,
  b15Map: Map<string, string>,
): void {
  if (!trim(model.controllerIncorporationStatute) && trim(model.controllerJurisdictionStatute)) {
    const combined = trim(model.controllerJurisdictionStatute);
    const dashParts = combined.split(/\s*[—–]\s*/);
    if (dashParts.length >= 2) {
      const jRaw = trim(dashParts[0]);
      model.controllerIncorporationStatute = dashParts.slice(1).map((p) => trim(p)).filter(Boolean).join(' — ');
      if (!trim(model.controllerJurisdictionCd) && /^B15\d+/i.test(jRaw)) {
        model.controllerJurisdictionCd = jRaw.toUpperCase();
      }
      model.controllerJurisdiction = resolveB15CodeToName(jRaw, b15Map) || jRaw;
    } else if (!trim(model.controllerJurisdictionCd)) {
      if (/^B15\d+/i.test(combined)) {
        model.controllerJurisdictionCd = combined.toUpperCase();
        model.controllerJurisdiction = resolveB15CodeToName(combined, b15Map);
      } else {
        model.controllerJurisdiction = combined;
      }
    }
  }

  if (
    !trim(model.controllerMobileNo)
    && !trim(model.controllerCountryCd)
    && trim(model.controllerContactNo)
  ) {
    const parsed = parseLegacyContactPhone(trim(model.controllerContactNo));
    model.controllerAreaCode = parsed.areaCode;
    model.controllerMobileNo = parsed.mobileNo;
    model.controllerCountryCd = parsed.countryCd;
  }
}
